<template>
  <div>
    <Button
      v-if="mapHidden"
      v-tooltip.right="{ value: $t(`MapLocationSelection.${getShowMap ? 'hideMap': 'showMap'}`) }"
      :class="['p-button-success map-toggler']"
      @click="handleToggleMap"
    >
      <i :class="`fa-regular fa-arrow-${getShowMap ? 'left' : 'right'}-from-bracket`" />
    </Button>
    <Form
      ref="form"
      class="flex-flow-row flex-gap-1"
      @error="handleValidationError"
    >
      <LocationInstruction
        v-if="canChangeLocation && (municipalityCode || getActiveMunicipality) && !getRecordLocked"
        :location-selection-allowed="locationSelectionAllowed"
      />
      <div class="flex-100">
        <MapLocationSelection
          :record="record"
          :values="values"
          :locked="getRecordLocked"
          :cancel-change="cancelChange"
          :can-change-location="canChangeLocation"
          :correction="correction"
          :asset-process-type="assetProcessType"
          :realisation-location-data="realisationLocationData"
          :location-edit-enabled="nextVersion || correction"
          :filters="activeFilters"
          :disabled="disabled || getRecordLocked"
          :municipality-code="municipalityCode || getActiveMunicipality"
          @setCoordinates="handleCoordinateChange"
          @locationConnected="handleConnectedLocation"
          @handleCancelChange="handleCancelChange"
          @setMunicipality="handleMunicipalityChange"
        />
      </div>
      <AutomatedViewsBtn
        v-if="!hasViews"
        class="flex-100"
        :uuid="uuid"
        :disabled="!record.uuid || getRecordLocked || disabled"
      />
      <div
        class="flex-100 flex-flow-row flex-gap-1 m-0"
      >
        <div class="flex-1">
          <FileUpload
            :key="fileUploadKey"
            :label="$t('processes.fields.topview')"
            doctype="TopView"
            :uuid="uuid"
            :files="getDocFiles({ type: 'TopView' })"
            :min-file-id="minTopViewDocsId"
            :reference="record.ref || null"
            :casefull="record.case_ref || null"
            :locked="getRecordLocked"
            :disabled="disabled || getRecordLocked"
            :preview="true"
            :wide="true"
            @files="handleFileChanges"
          />
          <p
            v-if="isFilesMessageShown({ type: 'TopView' })"
            class="text-muted"
          >
            {{ $t('processes.messages.no_topview_available') }}
          </p>
        </div>
        <div class="flex-1">
          <FileUpload
            :key="fileUploadKey"
            :label="$t('processes.fields.sideview')"
            doctype="SideView"
            :uuid="uuid"
            :files="getDocFiles({ type: 'SideView' })"
            :min-file-id="minSideViewDocsId"
            :reference="record.ref || null"
            :casefull="record.case_ref || null"
            :locked="getRecordLocked"
            :disabled="disabled || getRecordLocked"
            :preview="true"
            :wide="true"
            @files="handleFileChanges"
          />
          <p
            v-if="isFilesMessageShown({ type: 'SideView' })"
            class="text-muted"
          >
            {{ $t('processes.messages.no_sideview_available') }}
          </p>
        </div>
      </div>
    </Form>
  </div>
</template>

<script>

import Form from '@/components/form/Form.vue';
import MapLocationSelection from '@/components/map/MapLocationSelection.vue';
import AutomatedViewsBtn from '@/components/realisation/AutomatedViewsBtn.vue';
import LocationInstruction from '@/components/map/LocationInstruction.vue';
import FileUpload from '@/components/FileUpload.vue';
import { uuidValidateV4 } from '@/services/validation';
import { getGridOperatorByMunicipalityCode } from '@/services/gridoperators';
import { useStreetCabinetUploadField, finUnit } from '@/config';
import Button from 'primevue/button'
import Tooltip from 'primevue/tooltip'
import BaseStep from '@/views/processes/BaseStep.vue';

import { mapGetters } from 'vuex';

export default {
  name: 'ProcessMap',
  components: {
    MapLocationSelection,
    Form,
    AutomatedViewsBtn,
    FileUpload,
    Button,
    LocationInstruction
  },
  directives: {
    'tooltip': Tooltip,
  },
  mixins: [BaseStep],
  props: {
    connectionEnabled: {
      type: Boolean,
      default: true
    },
    municipalityCode: {
      type: String,
      default: ''
    },
    assetProcessType: {
      type: Object,
      default: null
    },
    mapHidden: {
      type: Boolean,
      default: false
    },
    canChangeLocation: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      nextVersion: false,
      activeFilters: [],
      StakeholderSelectionValues: {},
      canAddMapLocation: false,
      //municipalityCode: '',
      stakeHoldersKey: 0,
      locationSelectionAllowed: false,
      locationEditEnabled: true,
      cancelChange: false,
      chargingSituationKey: 0,
      isStreetCabinetAvailable: false,
      useStreetCabinetUploadField,
      finUnit,
      fileUploadKey: 0
    };
  },
  computed: {
    ...mapGetters('tenant', ['isCurrentTenant', 'getMunicipalityOptions']),
    ...mapGetters('chargingpoints', ['chargingpoints', 'getActiveMunicipality']),
    ...mapGetters('realisations', ['getSelectedRecord']),
    ...mapGetters('processes', ['getRecordLocked', 'getShowMap']),
    ...mapGetters('realisation', ['getSavedRecordValues', 'getActiveRecordValues']),

    // For existing records this is handled by the parent component, but not when it concerns new Records
    requestAdditions() {
      if (this.$route.query.requests && !this.hasRecord({ uuid: this.uuid })) {
        return this.$route.query.requests.split('|').filter(id => {
          return uuidValidateV4({ id }) && this.requestByUuid({ uuid: id });
        });
      }
      return [];
    },

    hasViews () {
      return ['TopView', 'SideView'].some(type => this.getDocFiles({ type }).length)
    },

    // Docs
    minParkingSpotsDocsId() {
      try {
        return this.minIdByDocType({ doctype: 'ParkingSpots' });
        // return this.record.values.Docs.ParkingSpots.reduce((result, id) => Math.max(result,id), 0)
      } catch (e) {
        return 0;
      }
    },
    minLocationPropositionDocsId() {
      try {
        return this.minIdByDocType({ doctype: 'LocationProposition' });
      } catch (e) {
        return 0;
      }
    },
    minStreetCabinetDocsId() {
      try {
        return this.minIdByDocType({ doctype: 'StreetCabinet' });
      } catch (e) {
        return 0;
      }
    },
    minTopViewDocsId() {
      try {
        let Max = this.minIdByDocType({ doctype: 'TopView' });
        let MaxAlt = this.minIdByDocType({ doctype: 'TopView-alt' });
        return Math.max(Max, MaxAlt);
      } catch (e) {
        return 0;
      }
    },
    minSideViewDocsId() {
      try {
        let Max = this.minIdByDocType({ doctype: 'SideView' });
        let MaxAlt = this.minIdByDocType({ doctype: 'SideView-alt' });
        return Math.max(Max, MaxAlt);
      } catch (e) {
        return 0;
      }
    },
    isCPO() {
      return this.hasRole({ role: 'cpo' });
    },

    routeType() {
      return this.$route.params.type || null;
    },
    gridOperator() {
      const code = this.municipalityCode;
      return getGridOperatorByMunicipalityCode({ code });
    },
    hasCpoAdvice() {
      return this.values.CPOAdvice.CPOAdvice;
    },
    isCorrection() {
      return this.correction && !!this.hasCpoAdvice;
    },
    // isRealisationPrivate () {
    //   return this.category === 'private'
    // },
    municipalityOptions () {
      return this.getMunicipalityOptions
    },
    recordUuid () {
      return this.$route.params?.uuid
    },
  },
  watch: {
    connectionEnabled: {
      handler (value) {
        this.locationEditEnabled = this.cancelChange = value
      }
    },
    requestAdditions() {
      this.addRequestsToRealisation();
    },
    /**
     * When the type changes, we need to clear the current form data.
     *  We do that by clearing the process type
     */
    routeType(newValue, oldValue) {
      if (newValue && oldValue) {
        this.category = null;
        this.$nextTick(() => {
          this.category = newValue;
        });
      } else if (newValue) {
        this.category = newValue;
      }
    },
    values: {
      deep: true,
      immediate: true,
      handler(val) {
        if (this.record.ref) {
          this.StakeholderSelectionValues = val;
        }
        // if (this.getActiveRecordValues) {
        //   const parkingSpots = val.ParkingSpots;
        //   this.handleParkingSpots({ parkingSpots });
        // }
      }
    },
    municipalityCode: {
      handler(code) {
        if (code) {
          this.locationSelectionAllowed = this.municipalityOptions.some(
            option => option.value === code && option.locationSelectionAllowed
          );
        }
      }
    },
    recordUuid: {
      handler() {
        this.fileUploadKey++
      }
    },
  },
  created() {
    // When using History.api, router sometimes unexpectedly lands on step 1 with different step value.
    // Could not find the reason why atm. so for now, force it to a appropriate step
    // if (this.$route.name.includes('realisationStep') && this.$route.params?.step !== '1') {
    //   this.$router.replace({ name: `realisationStep${this.$route.params.step}` }).catch(() => {});
    //   return;
    // }

    // if (this.routeType) {
    //   this.category = this.routeType;
    // }
    const record = this.getSelectedRecord;

    if (record) {
      //this.streetName = record.CurrentLocation.Location.StreetName
      this.values.Location = record.CurrentLocation.Location;

      this.addRequestsToRealisation();
      //this.category = record.category
    }

    if (this.values.Municipality) {
      //this.municipalityCode = this.values.Municipality;
    }

    if (this.useStreetCabinetUploadField) {
      this.isStreetCabinetAvailable = !!this.values.Docs.StreetCabinet?.length || !record;
    }
  },
  methods: {
    handleToggleMap () {
      this.showMap = !this.showMap
      this.$store.dispatch('processes/toggleProcessMap', { showMap: this.showMap })
    },
    // handleParkingSpots({ parkingSpots }) {
    //   if (parkingSpots) {
    //     if (this.getActiveRecordValues.ParkingSpots) {
    //       this.getActiveRecordValues.ParkingSpots.Identifiers = parkingSpots.Identifiers;
    //       this.$store.dispatch('realisation/setActiveRecordValues', { values: this.getActiveRecordValues });
    //     }
    //     if (this.values.ParkingSpots) {
    //       this.values.ParkingSpots.Identifiers = parkingSpots.Identifiers;
    //     }
    //   }
    // },

    handleConnectedLocation(location) {
      this.isLocationConnected = !!location?.Location;
      this.$emit('isLocationConnected', this.isLocationConnected)
      //this.handleParkingSpots({ parkingSpots: this.isLocationConnected ? location.ParkingSpots : { Identifiers: [] } });
      // this.$nextTick(() => {
      //   this.chargingSituationKey++;
      // });

      if (location) {
        this.$store.dispatch('realisation/setActiveRecordValues', { values: location });
      }
    },
    getDocFiles({ type }) {
      return this.getSelectedProcess?.Docs?.[type] || [];
    },

    isFilesMessageShown({ type }) {
      return (this.getRecordLocked || this.disabled) && this.getDocFiles({ type })?.length === 0;
    },

    prepData({ data }) {
      data.isStreetCabinetAvailable = this.isStreetCabinetAvailable && !!data.Docs?.StreetCabinet?.length;

      if (!this.isStreetCabinetAvailable && data.Docs?.StreetCabinet?.length) {
        data.Docs.StreetCabinet = [];
      }

      // Convert ParkingSpot Identifiers
      data.ParkingSpots.Identifiers = data.ParkingSpots.Identifiers || [];
      data.ParkingSpots.Identifiers = data.ParkingSpots.Identifiers.map(obj => obj.Identifier).filter(id => !!id);
      if (!data.FinUnit) {
        data.FinUnit = 'spv003'
      }

      if (this.isUnsavedRecord) {
        // The Process Type
        //data.category = this.category;
      }

      data.nextVersion = this.nextVersion;

      return data;
    },

    /**
     * Stub
     */
    isValidData({ data }) {
      if (!data.Location.Latitude || !data.Location.Longitude) {
        return {
          valid: false,
          feedback: 'danger',
          message: this.$i18n.t('RealisationStep1.locationRequired')
        }
      }

      if (this.goingToNextStep || this.correction) {
        if (
            data.LocationPreparation.RequiresLocationPreparation.includes('Overige') &&
            data.LocationPreparation.OtherReason === ''
          ) {
            return {
              valid: false,
              feedback: 'danger',
              message: 'Vul een korte omschrijving van de overige voorbereidingen in.'
            }
          }
      }

      return { valid: true };
    },
    // handleSetCategory({ category }) {
    //   this.record.setCategory({ category });
    //   this.category = category;
    //   history.replaceState(null, '', location.pathname.split('-')[0] + '-' + category);
    // },
    /**
     * Pass on the active filters
     */
    handleFilterRefresh({ filters }) {
      // , sorting, settings
      this.activeFilters = filters;
    },

    // eslint-disable-next-line no-unused-vars
    handleMunicipalityChange(code) {
      // if (
      //   this.canAddMapLocation &&
      //   !this.municipalityOptions.some(option => option.value === code && option.locationSelectionAllowed)
      // ) {
      //   return;
      // }

      // //this.municipalityCode = code;
      // this.setStakeholdersValues({ code })

    },
    handleCoordinateChange({ code }) {
      // Do not allow the municipality to be changed on stored records
      if (code && !this.record.ref) {
        if (code !== this.municipalityCode) {
          this.stakeHoldersKey++;
        }

        this.record.values.Location.Municipality = code;
        this.handleMunicipalityChange(code);
      }
    },
    // setStakeholdersValues ({ code }) {
    //   const municipalityOptions = this.isRealisationPrivate ? this.allMunicipalityOptions : this.municipalityOptions
    //   const [municipality] = municipalityOptions.filter(option => option.value === code.toString());
    //   const contractorByMunicipality = municipality?.contractor;
    //   this.record.values.Location.GridOperator = this.gridOperator;
    //   // Verify contractor
    //   const isMatchedContractorEnabled = this.getEnabledContractors.find(
    //     contractor => contractor.uuid === contractorByMunicipality
    //   );
    //   const currentContractor = this.record.values.Location.Contractor
    //   if (!currentContractor && isMatchedContractorEnabled) {
    //     this.record.values.Location.Contractor =
    //     this.getActiveRecordValues.Contractor = isMatchedContractorEnabled?.uuid || '';
    //   }

    //   this.getActiveRecordValues.Municipality = municipality?.value
    //   this.getActiveRecordValues.GridOperator = this.gridOperator;
    //   this.$store.dispatch('realisation/setActiveRecordValues', { values: this.getActiveRecordValues });
    //   this.StakeholderSelectionValues = this.getActiveRecordValues
    // }
  }
};
</script>
